<template>
  <div class="table-container">
    <div
      v-if="searchable"
      class="table-top"
    >
      <FormInputSearch
        :id="`${tableId}__input-search`"
        :value="searchValue"
        size="md"
        placeholder="Search for something"
        icon-placement="left"
        @input="onSearchInput"
      />

      <!-- @slot Slot to add a custom footer -->
      <slot name="top-extra" />
    </div>

    <BTable
      :id="tableId"
      ref="table"
      :table-class="{
        'has-top': searchable,
        'has-footer': perPage > 0,
      }"
      :fields="headers"
      :current-page="currentPage"
      :per-page="perPage"
      v-bind="$attrs"
      hover
      @row-selected="onRowSelected"
      v-on="listeners"
    >
      <template v-slot:head(_selectable)>
        <FormCheckbox
          :id="`${tableId}__toggle-select-all-checkbox`"
          :checked="isSelectedAll"
          :indeterminate.sync="indeterminate"
          @change="toggleSelectAll"
        />
      </template>

      <template v-slot:cell(_selectable)="row">
        <FormCheckbox
          :id="getItemCheckboxId(row)"
          :checked="row.rowSelected"
          :disabled="row.item._rowVariant === 'disabled'"
          @change="(value) => !value ? row.unselectRow() : row.selectRow()"
        />
      </template>

      <template
        v-if="expandable"
        v-slot:[expandedSlot]="row"
      >
        <span class="icon-expanded">
          <SvgIcon
            :name="row.detailsShowing ? 'components/chevron-up-alt2' : 'components/chevron-down-alt2'"
            width="16"
            height="16"
            @click="row.toggleDetails"
          />
        </span>

        <slot
          :name="expandedSlot"
          :row="row"
        >
          {{ row.value }}
        </slot>
      </template>

      <template
        v-for="(_, slot) of $scopedSlots"
        v-slot:[slot]="scope"
      >
        <slot
          :name="slot"
          v-bind="scope"
        />
      </template>
    </BTable>

    <div
      v-if="perPage > 0"
      class="table-footer"
    >
      <div class="row">
        <div class="col-6">
          <Pagination
            v-model="currentPage"
            :total-rows="totalItems"
            :per-page="perPage"
          />
        </div>
        <div class="col-6 text-right">
          Showing {{ paginationData.start }}-{{ paginationData.end }} of {{ paginationData.total }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SvgIcon from 'vue-svgicon/dist/components/svgicon.common'
import {BTable} from 'bootstrap-vue'
import idMixin from 'bootstrap-vue/src/mixins/id'

import {FormInputSearch} from '../FormInputSearch'
import {FormCheckbox} from '../FormCheckboxInputs'
import {Pagination} from '../Pagination'

import '../../icons/components/chevron-down-alt2'
import '../../icons/components/chevron-up-alt2'

export default {
  components: {
    BTable,
    SvgIcon,
    FormInputSearch,
    FormCheckbox,
    Pagination,
  },

  mixins: [idMixin],

  inheritAttrs: false,

  props: {
    /**
     * Array of field names or array of field definition objects
     */
    fields: {
      type: Array,
      required: true,
      default: () => ([]),
    },

    /**
     * Shows the expand toggle in default rows
     */
    expandable: {
      type: Boolean,
      default: false,
    },

    /**
     * Show search input
     */
    searchable: {
      type: Boolean,
      default: false,
    },

    /**
     * Total number of item. Required when using Pagination.
     */
    totalItems: {
      type: [String, Number],
      default: 0,
    },
  },

  data: () => ({
    searchValue: '',
    isSelectedAll: false,
    selected: [],
    currentPage: 1,
  }),

  computed: {
    listeners () {
      const {rowSelected, ...listeners} = this.$listeners
      return listeners
    },
    tableId () {
      return this.id || this.localId_
    },
    perPage () {
      return Number(this.$attrs.perPage || this.$attrs['per-page']) || 0
    },
    paginationData () {
      const start = (this.perPage * this.currentPage) - (this.perPage - 1)
      const end = this.perPage * this.currentPage
      const total = this.totalItems

      return {
        start,
        end: end > total ? total : end,
        total,
      }
    },
    indeterminate: {
      get: function () {
        const itemsLength = typeof this.$attrs.items === 'function' ? this.totalItems : this.$attrs.items.length

        if (this.selected.length === 0) {
          return false
        } if (this.selected.length === itemsLength) {
          return false
        } else {
          return true
        }
      },
      set: function (newValue) {
        console.debug('indeterminate setter', newValue)
      },
    },
    headers () {
      const fields = [...this.fields]

      if (this.$attrs.selectable === '') {
        fields.unshift({key: '_selectable', label: '', class: 'checkbox'})
      }

      return fields
    },
    expandedSlot () {
      return 'cell(' + this.headers[0].key + ')'
    },
  },

  methods: {
    onRowSelected (items) {
      this.selected = items

      this.$emit('row-selected', items)
    },

    toggleSelectAll () {
      this.isSelectedAll = !this.isSelectedAll

      if (this.isSelectedAll) {
        this.$refs.table.selectAllRows()
      } else {
        this.$refs.table.clearSelected()
      }
    },

    onSearchInput (value) {
      /**
       * Event triggered by user interaction. Emitted after the search value is updated
       */
      this.$emit('search', value)
    },

    getItemCheckboxId (row) {
      const rowId = row.item[this.$attrs.primaryKey || this.$attrs['primary-key']]
      let id = `${this.tableId}__row_`

      if (rowId) {
        id += `${rowId}`
      } else {
        id += `${row.index}`
      }

      return `${id}-checkbox`
    },
  },
}
</script>

<style lang="scss">
$data-table-bg-color: $gray-100 !default;

.table-container {
  display: block;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 1rem;

  .table {
    margin-bottom: 0;
    color: $table-color;

    &.has-footer {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    &.has-top {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }

    th,
    td {
      &.text {
        &-left {
          text-align: left;
        }

        &-center {
          text-align: center;
        }

        &-right {
          text-align: right;
        }
      }
    }

    thead th {
      .custom-checkbox {
        .custom-control-label {
          margin-top: 2px;
        }
      }
    }

    tbody {
      tr {
        &.b-table-has-details {
          background-color: $data-table-bg-color;
        }

        &.table-disabled {
          pointer-events: none;
          background-color: $data-table-bg-color;
          color: $gray-300;
        }

        &.b-table-details {
          background-color: $data-table-bg-color;

          td {
            padding: 8px 48px 24px 52px;
            border-top: none;
          }
        }

        td {
          .icon-expanded {
            cursor: pointer;
            margin-right: 16px;
          }

          .btn-light {
            background-color: transparent;
            border-color: transparent;
          }
        }
      }
    }

    &-footer {
      font-size: 14px;
      background-color: #fff;
      border: 1px solid $gray-200;
      border-top: none;
      border-bottom-left-radius: 2px;
      border-bottom-right-radius: 2px;
      padding: 11px 22px 11px 0;

      .pagination {
        margin-bottom: 0;
      }
    }

    &-top {
      background-color: #fff;
      border: 1px solid $gray-200;
      border-bottom: none;
      padding: 3.5px 20px 3.5px 0;
      display: flex;
      align-items: center;
      font-size: 14px;
      border-top-left-radius: 2px;
      border-top-right-radius: 2px;

      .form-input-search {
        border: none;
        flex-grow: 1;
      }
    }
  }
}
</style>
